import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80';

// public/index.html starts the config request before this bundle has even
// downloaded. Take that request over (once) instead of sending another.
const takePrefetchedConfig = () => {
  const request = window.__homepageConfig;
  window.__homepageConfig = undefined;
  return request;
};

const fetchConfig = () => axios.get(`${API_BASE_URL}/homepage/config`).then(res => res.data);

const heroStyles = `
@keyframes kenBurns {
  0%   { transform: scale(1.0) translate(0%, 0%); }
  25%  { transform: scale(1.15) translate(-2%, -1%); }
  50%  { transform: scale(1.1) translate(1%, -2%); }
  75%  { transform: scale(1.2) translate(-1%, 1%); }
  100% { transform: scale(1.0) translate(0%, 0%); }
}
@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pulseGlow {
  0%, 100% { opacity: 0.3; }
  50%      { opacity: 0.6; }
}
.hero-bg-animate {
  animation: kenBurns 25s ease-in-out infinite;
  will-change: transform;
}
.hero-fade-in { opacity: 0; animation: fadeSlideUp 0.9s ease-out forwards; }
.hero-fade-in-1 { animation-delay: 0.2s; }
.hero-fade-in-2 { animation-delay: 0.5s; }
.hero-fade-in-3 { animation-delay: 0.8s; }
.hero-fade-in-4 { animation-delay: 1.1s; }
.hero-vignette {
  box-shadow: inset 0 0 150px 60px rgba(0,0,0,0.3);
  pointer-events: none;
}
.hero-glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: pulseGlow 6s ease-in-out infinite;
  pointer-events: none;
}
.hero-min-h { min-height: 92vh; }
@media (max-width: 640px) {
  .hero-min-h { min-height: 82vh; }
}
`;

const Home = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [collections, setCollections] = useState([]);
  const [categoryCollections, setCategoryCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [scrollEdges, setScrollEdges] = useState({});
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  const collectionRefs = useRef({});
  const categoryCollectionRefs = useRef({});

  useEffect(() => {
    fetchHomeData();
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handlers = [];
    const checkEdges = (id, el) => {
      setScrollEdges(prev => ({
        ...prev,
        [id]: {
          atStart: el.scrollLeft <= 0,
          atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
        }
      }));
    };

    const allCollections = [
      ...collections.map(c => ({ id: c.id, refs: collectionRefs })),
      ...categoryCollections.map(c => ({ id: c.id, refs: categoryCollectionRefs }))
    ];

    allCollections.forEach(({ id, refs }) => {
      const el = refs.current[id];
      if (!el) return;
      const handler = () => checkEdges(id, el);
      handler();
      el.addEventListener('scroll', handler, { passive: true });
      handlers.push({ el, handler });
    });

    return () => handlers.forEach(({ el, handler }) => el.removeEventListener('scroll', handler));
  }, [collections, categoryCollections]);

  const fetchHomeData = async () => {
    try {
      setLoading(true);

      const cached = sessionStorage.getItem('home_data');
      if (cached) {
        const { config: cachedConfig, collections: cachedCollections, categoryCollections: cachedCategoryCollections } = JSON.parse(cached);
        setConfig(cachedConfig);
        setCollections(cachedCollections);
        setCategoryCollections(cachedCategoryCollections);
        setLoading(false);
        return;
      }

      // Categories don't depend on the config, so request both at once. The
      // hero renders as soon as the config is set (see the loading check below).
      const categoriesRequest = axios.get(`${API_BASE_URL}/products/categories`)
        .then(res => res.data.filter(cat => cat.is_active))
        .catch(err => {
          console.error('Failed to fetch categories:', err);
          return [];
        });
      const prefetchedConfig = takePrefetchedConfig();
      const configData = prefetchedConfig
        ? await prefetchedConfig.catch(fetchConfig)
        : await fetchConfig();
      setConfig(configData);

      const allCategories = await categoriesRequest;

      // Process Category Collections
      let finalCategoryCollections = [];
      if (configData.category_collections && configData.category_collections.length > 0) {
        finalCategoryCollections = configData.category_collections
          .slice()
          .sort((a, b) => a.order - b.order)
          .map(collection => {
            const collectionCategories = (collection.categories || [])
              .filter(cc => cc.visible)
              .sort((a, b) => a.order - b.order)
              .map(cc => cc.category_data || allCategories.find(c => c.id === cc.category_id))
              .filter(c => c !== undefined);
              
            return {
              ...collection,
              items: collectionCategories
            };
          }).filter(c => c.visible && c.items.length > 0);
        
        setCategoryCollections(finalCategoryCollections);
      } else {
        setCategoryCollections([]);
      }

      // Process Product Collections
      let finalCollections = [];
      if (configData.collections && configData.collections.length > 0) {
        let allProducts = [];
        const needsProductFetch = configData.collections.some(c => 
          c.products.some(p => !p.product_data)
        );

        if (needsProductFetch) {
          const productsResponse = await axios.get(`${API_BASE_URL}/products?active=true&limit=200`);
          allProducts = productsResponse.data.products || productsResponse.data;
        }

        finalCollections = configData.collections
          .slice()
          .sort((a, b) => a.order - b.order)
          .map(collection => {
            const collectionProducts = collection.products
              .filter(cp => cp.visible)
              .sort((a, b) => a.order - b.order)
              .map(cp => cp.product_data || allProducts.find(p => p.id === cp.product_id))
              .filter(p => p !== undefined);
              
            return {
              ...collection,
              items: collectionProducts
            };
          }).filter(c => c.visible && c.items.length > 0);
        
        setCollections(finalCollections);
      } else {
        setCollections([]);
      }

      try {
        sessionStorage.setItem('home_data', JSON.stringify({
          config: configData,
          collections: finalCollections,
          categoryCollections: finalCategoryCollections
        }));
      } catch (_) {}

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch home data:', error);
      setLoading(false);
    }
  };

  const scrollCollection = (id, direction) => {
    const container = collectionRefs.current[id];
    if (container) {
      const scrollAmount = container.clientWidth * 0.75;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollCategoryCollection = (id, direction) => {
    const container = categoryCollectionRefs.current[id];
    if (container) {
      const scrollAmount = container.clientWidth * 0.75;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || newsletterStatus === 'loading') return;
    setNewsletterStatus('loading');
    try {
      const res = await axios.post(`${API_BASE_URL}/newsletter/subscribe`, { email: newsletterEmail });
      setNewsletterStatus('success');
      setNewsletterMessage(res.data.message || 'Thanks for subscribing!');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterMessage(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  // Only the hero needs the config; show it without waiting for the collections.
  if (loading && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="home bg-white text-gray-900 font-sans">
      <style>{heroStyles}</style>

      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden hero-min-h">
        {/* Animated Background Image */}
        <div className="absolute inset-0">
          {/* A real <img> (not a CSS background) so the browser can prioritize it
              and reuse the preload started in public/index.html. */}
          <img
            src={config?.hero_image || DEFAULT_HERO_IMAGE}
            alt=""
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover hero-bg-animate"
          />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/40" />
          <div className="absolute inset-0 hero-vignette" />
        </div>

        {/* Soft Glow Orbs */}
        <div
          className="hero-glow-orb"
          style={{ width: 400, height: 400, top: '10%', left: '-5%', background: 'rgba(255,255,255,0.06)' }}
        />
        <div
          className="hero-glow-orb"
          style={{ width: 300, height: 300, bottom: '5%', right: '-3%', background: 'rgba(255,255,255,0.04)', animationDelay: '3s' }}
        />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-5 md:px-6 text-center text-white max-w-4xl">
          {heroLoaded && (
            <>
              <h1 className="hero-fade-in hero-fade-in-1 text-[2.4rem] sm:text-5xl md:text-7xl font-light mb-4 md:mb-6 tracking-tight leading-tight">
                {config?.hero_title || "Design Your Sanctuary"}
              </h1>
              <p className="hero-fade-in hero-fade-in-2 text-sm sm:text-lg md:text-2xl mb-7 md:mb-10 text-gray-200 font-light max-w-2xl mx-auto leading-relaxed">
                {config?.hero_subtitle || 'Curated furniture and decor for the modern home.'}
              </p>
              <div className="hero-fade-in hero-fade-in-3 flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center w-full max-w-xs sm:max-w-none mx-auto">
                <Link
                  to="/products"
                  className="w-full sm:w-auto px-8 md:px-10 py-3.5 md:py-4 bg-white text-black font-medium rounded-full hover:bg-gray-100 transition-all transform hover:-translate-y-1 shadow-xl hover:shadow-2xl sm:min-w-[180px] text-center text-sm md:text-base"
                >
                  Shop All
                </Link>
                <Link
                  to="/products?featured=true"
                  className="w-full sm:w-auto px-8 md:px-10 py-3.5 md:py-4 bg-transparent border border-white/80 text-white font-medium rounded-full hover:bg-white/10 transition-all backdrop-blur-sm sm:min-w-[180px] text-center text-sm md:text-base"
                >
                  Featured
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-7 md:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Keeps the sections below out of view until the collections arrive, so
          they don't jump down the page when the collections render above them. */}
      {loading && <div className="min-h-screen" aria-hidden="true" />}

      {/* Interleaved Product & Category Collections */}
      {(() => {
        const interleaved = [];
        const maxLen = Math.max(collections.length, categoryCollections.length);
        for (let i = 0; i < maxLen; i++) {
          if (i < collections.length) interleaved.push({ type: 'product', data: collections[i] });
          if (i < categoryCollections.length) interleaved.push({ type: 'category', data: categoryCollections[i] });
        }
        return interleaved.map((entry) => {
          const collection = entry.data;
          if (entry.type === 'product') {
            return (
              <section key={collection.id} className="py-8 md:py-14 bg-white border-t border-gray-100">
                <div className="container mx-auto px-4 md:px-6">
                  <div className="mb-5 md:mb-8">
                    <div className="max-w-2xl">
                      <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-2">{collection.title}</h2>
                      {collection.description && (
                        <p className="text-gray-500 text-base font-light">{collection.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative flex items-center">
                    {!scrollEdges[collection.id]?.atStart && (
                      <button 
                        onClick={() => scrollCollection(collection.id, 'left')}
                        className="hidden md:flex absolute -left-5 z-10 p-3 rounded-full border border-gray-200 bg-white/80 backdrop-blur hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-md"
                        aria-label="Scroll left"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                      </button>
                    )}

                    <div 
                      ref={el => collectionRefs.current[collection.id] = el}
                      className="flex gap-3 md:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 md:pb-8 -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth w-full"
                    >
                    {collection.items.map(product => (
                      <div 
                        key={product.id} 
                        className="min-w-[155px] max-w-[155px] md:min-w-[260px] md:max-w-[260px] snap-start group cursor-pointer flex-shrink-0"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        <div className="relative aspect-[3/4] overflow-hidden bg-white rounded-lg mb-4 shadow-sm">
                          {product.images && product.images[0] ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.name} 
                              className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                              draggable="false"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                          )}
                          
                          {product.images && product.images[1] && (
                            <img 
                              src={product.images[1]} 
                              alt={`${product.name} view 2`} 
                              className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                              draggable="false"
                              loading="lazy"
                              decoding="async"
                            />
                          )}
                          
                          <div className="absolute top-3 left-3 flex flex-col gap-2">
                            {(() => {
                              const now = new Date();
                              const sOk = !product.sale_start_date || new Date(product.sale_start_date) <= now;
                              const eOk = !product.sale_end_date || new Date(product.sale_end_date) >= now;
                              const onSale = product.sale_price && product.sale_price > 0 && sOk && eOk && product.sale_price < product.price;
                              return onSale ? (
                                <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider rounded-sm shadow-sm">
                                  Sale
                                </span>
                              ) : null;
                            })()}
                            {product.quantity_discounts && product.quantity_discounts.length > 0 && (
                              <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider rounded-sm shadow-sm">
                                Bulk Savings
                              </span>
                            )}
                            {product.stock <= 5 && product.stock > 0 && (
                              <span className="bg-white/90 backdrop-blur text-orange-600 text-xs font-bold px-3 py-1 uppercase tracking-wider rounded-sm shadow-sm">
                                Low Stock
                              </span>
                            )}
                          </div>

                          <div className="absolute bottom-4 inset-x-4 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 hidden md:block">
                            <button className="w-full bg-white text-gray-900 font-medium py-3 rounded shadow-lg hover:bg-black hover:text-white transition-colors">
                              Quick View
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-1 text-center md:text-left">
                          <h3 className="text-sm md:text-base font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
                            {product.name}
                          </h3>
                          <div className="flex items-center justify-center md:justify-start gap-2">
                            {(() => {
                              const now = new Date();
                              const sOk = !product.sale_start_date || new Date(product.sale_start_date) <= now;
                              const eOk = !product.sale_end_date || new Date(product.sale_end_date) >= now;
                              const onSale = product.sale_price && product.sale_price > 0 && sOk && eOk && product.sale_price < product.price;
                              return onSale ? (
                                <>
                                  <span className="font-semibold text-red-600 text-sm md:text-base">${product.sale_price.toFixed(2)}</span>
                                  <span className="text-gray-400 line-through text-xs md:text-sm">${product.price.toFixed(2)}</span>
                                </>
                              ) : (
                                <span className="font-semibold text-gray-900 text-sm md:text-base">${product.price.toFixed(2)}</span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>

                    {!scrollEdges[collection.id]?.atEnd && (
                      <button 
                        onClick={() => scrollCollection(collection.id, 'right')}
                        className="hidden md:flex absolute -right-5 z-10 p-3 rounded-full border border-gray-200 bg-white/80 backdrop-blur hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-md"
                        aria-label="Scroll right"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                      </button>
                    )}
                    {!scrollEdges[collection.id]?.atEnd && (
                      <div className="absolute right-0 top-0 bottom-4 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none md:hidden z-10" />
                    )}
                  </div>
                </div>
              </section>
            );
          } else {
            return (
              <section key={collection.id} className="py-10 md:py-24 bg-[#00396C]">
                <div className="container mx-auto px-4 md:px-6">
                  <div className="mb-6 md:mb-12">
                    <div className="max-w-2xl">
                      <h2 className="text-2xl md:text-5xl font-light text-white mb-2 md:mb-4">{collection.title}</h2>
                      {collection.description && (
                        <p className="text-gray-300 text-sm md:text-lg font-light">{collection.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative flex items-center">
                    {!scrollEdges[collection.id]?.atStart && (
                      <button 
                        onClick={() => scrollCategoryCollection(collection.id, 'left')}
                        className="hidden md:flex absolute -left-5 z-10 p-3 rounded-full border border-white/30 bg-[#00396C]/80 backdrop-blur hover:bg-white/10 transition-colors focus:outline-none"
                        aria-label="Scroll left"
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                      </button>
                    )}

                    <div 
                      ref={el => categoryCollectionRefs.current[collection.id] = el}
                      className="flex gap-3 md:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 md:pb-8 -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth w-full"
                    >
                      {collection.items.map((category) => (
                        <div 
                          key={category.id} 
                          className="min-w-[230px] max-w-[230px] md:min-w-[420px] md:max-w-[420px] snap-start group cursor-pointer flex-shrink-0"
                          onClick={() => navigate(`/products?category=${category.id}`)}
                        >
                          <div className="relative aspect-square overflow-hidden shadow-sm rounded-t-lg">
                            {category.image ? (
                              <img 
                                src={category.image} 
                                alt={category.name} 
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                draggable="false"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 font-medium">No Image</span>
                              </div>
                            )}
                          </div>
                          <div className="relative overflow-hidden rounded-b-lg py-3 px-4 text-center bg-white group-hover:bg-[#00396C] transition-colors duration-500">
                            <h3 className="text-lg font-semibold text-[#00396C] group-hover:text-white transition-colors duration-500 tracking-wide group-hover:tracking-widest">Shop {category.name}</h3>
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] w-0 group-hover:w-2/3 bg-white/70 transition-all duration-500 ease-out"></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {!scrollEdges[collection.id]?.atEnd && (
                      <button 
                        onClick={() => scrollCategoryCollection(collection.id, 'right')}
                        className="hidden md:flex absolute -right-5 z-10 p-3 rounded-full border border-white/30 bg-[#00396C]/80 backdrop-blur hover:bg-white/10 transition-colors focus:outline-none"
                        aria-label="Scroll right"
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                      </button>
                    )}
                    {!scrollEdges[collection.id]?.atEnd && (
                      <div className="absolute right-0 top-0 bottom-4 w-10 bg-gradient-to-l from-[#00396C] to-transparent pointer-events-none md:hidden z-10" />
                    )}
                  </div>
                </div>
              </section>
            );
          }
        });
      })()}

      {/* Modern Features Banner */}
      <section className="py-8 md:py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-3 gap-3 md:gap-10">
            <div className="text-center p-2 md:p-6">
              <div className="w-10 h-10 md:w-16 md:h-16 mx-auto bg-white rounded-full flex items-center justify-center mb-3 md:mb-6 shadow-sm text-primary">
                <svg className="w-4 h-4 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2z" />
                </svg>
              </div>
              <h3 className="font-bold text-[10px] md:text-lg mb-1 md:mb-3 leading-tight">Premium Quality</h3>
              <p className="hidden md:block text-gray-500 font-light leading-relaxed">Crafted with the finest materials to ensure lasting beauty and durability for your home.</p>
            </div>
            <div className="text-center p-2 md:p-6">
              <div className="w-10 h-10 md:w-16 md:h-16 mx-auto bg-white rounded-full flex items-center justify-center mb-3 md:mb-6 shadow-sm text-primary">
                <svg className="w-4 h-4 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
                  <polyline points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              </div>
              <h3 className="font-bold text-[10px] md:text-lg mb-1 md:mb-3 leading-tight">Free Shipping</h3>
              <p className="hidden md:block text-gray-500 font-light leading-relaxed">Free shipping on orders of $100 or more.</p>
            </div>
            <div className="text-center p-2 md:p-6">
              <div className="w-10 h-10 md:w-16 md:h-16 mx-auto bg-white rounded-full flex items-center justify-center mb-3 md:mb-6 shadow-sm text-primary">
                <svg className="w-4 h-4 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3 className="font-bold text-[10px] md:text-lg mb-1 md:mb-3 leading-tight">Satisfaction Guaranteed</h3>
              <p className="hidden md:block text-gray-500 font-light leading-relaxed">Love it or return it. We stand behind every piece we sell with our guarantee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter - Minimalist */}
      <section className="py-10 md:py-24 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center">
          <h2 className="text-2xl md:text-5xl font-light mb-3 md:mb-6">Join the Inner Circle</h2>
          <p className="text-gray-400 mb-6 md:mb-10 max-w-2xl mx-auto text-sm md:text-lg font-light">
            Be the first to know about new collections, exclusive events, and design inspiration.
          </p>
          {newsletterStatus === 'success' ? (
            <div className="flex items-center justify-center gap-2 text-green-400 text-sm md:text-base">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
              <span>{newsletterMessage}</span>
            </div>
          ) : (
            <>
              <form className="flex flex-col sm:flex-row gap-3 md:gap-4 max-w-md mx-auto" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  placeholder="Email address"
                  value={newsletterEmail}
                  onChange={(e) => { setNewsletterEmail(e.target.value); setNewsletterStatus(null); }}
                  className="flex-1 px-5 md:px-6 py-3.5 md:py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm transition-all text-sm md:text-base"
                  required
                  disabled={newsletterStatus === 'loading'}
                />
                <button 
                  type="submit" 
                  className="px-7 md:px-8 py-3.5 md:py-4 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-colors shadow-lg text-sm md:text-base disabled:opacity-50"
                  disabled={newsletterStatus === 'loading'}
                >
                  {newsletterStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {newsletterStatus === 'error' && (
                <p className="mt-3 text-red-400 text-sm">{newsletterMessage}</p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
